import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, Etoiles, type IconName } from './Icon';

/**
 * ATELIER D'INTERFACE — les seules briques autorisées.
 *
 * La règle de la maison : un écran n'invente ni bouton, ni champ, ni panneau.
 * Sept primitives suffisent, et parce qu'elles sont peu nombreuses, elles se
 * ressemblent partout — c'est ce qui distingue une boutique d'un assemblage.
 *
 * Deux principes de la revue sont câblés ici, pas laissés à la bonne volonté :
 *   · **action ≠ sélecteur** : un bouton est rouge et plein, un sélecteur est un
 *     panneau clair avec un état coché ; jamais l'inverse ;
 *   · **un champ se lit** : libellé au-dessus, 52 px de haut, une aide sous le
 *     champ, une erreur qui dit quoi faire — et un focus visible à 3 px.
 */

export const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/* ==================================================================== BOUTON */

type Variante = 'primary' | 'ink' | 'outline' | 'quiet' | 'ghost';

interface BoutonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: Variante;
  taille?: 'sm' | 'md' | 'lg';
  bloc?: boolean;
  icone?: IconName;
  iconeDroite?: IconName;
}

export function Bouton({ variante = 'primary', taille = 'md', bloc, icone, iconeDroite, className, children, ...rest }: BoutonProps) {
  return (
    <button
      className={cn('btn', `btn--${variante}`, taille !== 'md' && `btn--${taille}`, bloc && 'btn--block', className)}
      {...rest}
    >
      {icone && <Icon name={icone} size={18} />}
      {children}
      {iconeDroite && <Icon name={iconeDroite} size={18} />}
    </button>
  );
}

/** La même apparence, pour un `<Link>` : un lien reste un lien. */
export const classeBouton = (variante: Variante = 'primary', extra = '') =>
  cn('btn', `btn--${variante}`, extra);

/* ============================================================== ICÔNE-BOUTON */

export function IconeBouton({ nom, libelle, actif, className, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { nom: IconName; libelle: string; actif?: boolean }) {
  return (
    <button type="button" className={cn('icon-btn', className)} aria-label={libelle} title={libelle}
      aria-pressed={actif} {...rest}>
      <Icon name={nom} size={21} />
    </button>
  );
}

/* ================================================================ ÉTIQUETTE */

export function Etiquette({ children, ton = 'neutre', className }: {
  children: ReactNode;
  ton?: 'neutre' | 'laiton' | 'vin' | 'succes' | 'alerte';
  className?: string;
}) {
  const tons: Record<string, string> = {
    neutre: 'bg-lin-100 text-encre-700',
    laiton: 'bg-laiton-50 text-laiton-800',
    vin: 'bg-wine-wash text-wine-deep',
    succes: 'bg-success-wash text-success',
    alerte: 'bg-alert-wash text-alert',
  };
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.6875rem] font-medium tracking-wide', tons[ton], className)}>
      {children}
    </span>
  );
}

/* =================================================================== ÉTOILES */

export function Note({ note, avis, taille = 14, discret, compact }: {
  note: number; avis?: number; taille?: number; discret?: boolean; compact?: boolean;
}) {
  const sobre = discret ?? compact;
  return (
    <span className={cn('inline-flex items-center gap-2', sobre ? 'text-[0.75rem] text-encre-500' : 'text-[0.8125rem] text-encre-600')}>
      <Etoiles note={note} taille={taille} />
      <span className="tabular-nums">{note.toFixed(1).replace('.', ',')}</span>
      {typeof avis === 'number' && <span className="text-encre-500">· {avis} avis</span>}
    </span>
  );
}

/* ================================================================== CHAMPS */

function Champ({ label, aide, erreur, obligatoire, htmlFor, children }: {
  label: string; aide?: string; erreur?: string; obligatoire?: boolean; htmlFor: string; children: ReactNode;
}) {
  return (
    <div className={cn('field', erreur && 'field--error')}>
      <label className="field__label" htmlFor={htmlFor}>
        {label}{obligatoire && <span className="ml-1 text-wine" aria-hidden="true">*</span>}
      </label>
      {children}
      {erreur
        ? <p className="field__error" role="alert"><Icon name="info" size={15} />{erreur}</p>
        : aide ? <p className="field__hint">{aide}</p> : null}
    </div>
  );
}

export function Saisie({ label, aide, erreur, obligatoire, className, ...rest }:
  InputHTMLAttributes<HTMLInputElement> & { label: string; aide?: string; erreur?: string; obligatoire?: boolean }) {
  const id = useId();
  return (
    <Champ label={label} aide={aide} erreur={erreur} obligatoire={obligatoire} htmlFor={rest.id ?? id}>
      <input id={rest.id ?? id} className={cn('input', className)} aria-invalid={!!erreur}
        aria-describedby={erreur ? `${id}-err` : undefined} {...rest} />
    </Champ>
  );
}

export function Liste({ label, aide, erreur, obligatoire, options, className, ...rest }:
  SelectHTMLAttributes<HTMLSelectElement> & {
    label: string; aide?: string; erreur?: string; obligatoire?: boolean;
    options: Array<{ valeur: string; libelle: string }>;
  }) {
  const id = useId();
  return (
    <Champ label={label} aide={aide} erreur={erreur} obligatoire={obligatoire} htmlFor={rest.id ?? id}>
      <select id={rest.id ?? id} className={cn('select', className)} aria-invalid={!!erreur} {...rest}>
        {options.map(o => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
      </select>
    </Champ>
  );
}

export function ZoneTexte({ label, aide, erreur, className, ...rest }:
  TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; aide?: string; erreur?: string }) {
  const id = useId();
  return (
    <Champ label={label} aide={aide} erreur={erreur} htmlFor={rest.id ?? id}>
      <textarea id={rest.id ?? id} className={cn('textarea', className)} aria-invalid={!!erreur} {...rest} />
    </Champ>
  );
}

export function CaseACocher({ label, sousTitre, className, ...rest }:
  InputHTMLAttributes<HTMLInputElement> & { label: string; sousTitre?: string }) {
  const id = useId();
  return (
    <label className={cn('choice', className)} htmlFor={id}>
      <input id={id} type="checkbox" {...rest} />
      <span className="choice__text">
        <strong className="font-semibold">{label}</strong>
        {sousTitre && <span className="choice__sub">{sousTitre}</span>}
      </span>
    </label>
  );
}

/* ======================================================= SEGMENTÉ / PUCES */

export function Segmente({ options, valeur, onChange, libelle, bloc }: {
  options: Array<{ valeur: string; libelle: string; icone?: IconName }>;
  valeur: string; onChange: (v: string) => void; libelle: string; bloc?: boolean;
}) {
  return (
    <div className={cn('seg', bloc && 'seg--block')} role="group" aria-label={libelle}>
      {options.map(o => (
        <button key={o.valeur} type="button" className="seg__btn" aria-pressed={valeur === o.valeur}
          onClick={() => onChange(o.valeur)}>
          <span className="inline-flex items-center gap-2">
            {o.icone && <Icon name={o.icone} size={16} />}{o.libelle}
          </span>
        </button>
      ))}
    </div>
  );
}

export function Puce({ actif, children, onClick, supprimable, libelleSuppression }: {
  actif?: boolean; children: ReactNode; onClick?: () => void;
  supprimable?: boolean; libelleSuppression?: string;
}) {
  return (
    <button type="button" className={cn('chip', supprimable && 'chip--removable')}
      aria-pressed={actif} onClick={onClick}>
      {children}
      {supprimable && (
        <span className="inline-flex items-center" aria-label={libelleSuppression || 'Retirer'}>
          <Icon name="close" size={14} />
        </span>
      )}
    </button>
  );
}

/* ================================================================= COMPTEUR */

export function Compteur({ valeur, min = 1, max = 20, onChange, libelle, taille = 'md' }: {
  valeur: number; min?: number; max?: number; onChange: (v: number) => void;
  libelle: string; taille?: 'sm' | 'md';
}) {
  const cote = taille === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
  return (
    <div className="inline-flex items-center rounded-full bg-surface shadow-soft ring-1 ring-lin-200">
      <button type="button" className={cn(cote, 'grid place-items-center rounded-full text-encre-600 transition hover:bg-lin-100 disabled:opacity-35')}
        onClick={() => onChange(Math.max(min, valeur - 1))} disabled={valeur <= min} aria-label={`Diminuer ${libelle}`}>
        <Icon name="minus" size={16} />
      </button>
      <span className={cn('min-w-8 text-center font-semibold tabular-nums', taille === 'sm' ? 'text-sm' : 'text-base')}
        aria-live="polite">{valeur}</span>
      <button type="button" className={cn(cote, 'grid place-items-center rounded-full text-encre-600 transition hover:bg-lin-100 disabled:opacity-35')}
        onClick={() => onChange(Math.min(max, valeur + 1))} disabled={valeur >= max} aria-label={`Augmenter ${libelle}`}>
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}

/* ================================================================= FEUILLES */

/** Piège de focus : dans une feuille, la tabulation tourne en rond. */
function piegeDeFocus(racine: HTMLElement, e: KeyboardEvent) {
  const selecteurs = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const elements = Array.from(racine.querySelectorAll<HTMLElement>(selecteurs)).filter(x => x.offsetParent !== null);
  if (!elements.length) return;
  const premier = elements[0];
  const dernier = elements[elements.length - 1];
  if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
  else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
}

export function Feuille({ ouverte, onFermer, titre, cote = 'right', children, pied, sousTitre }: {
  ouverte: boolean; onFermer: () => void; titre: string;
  cote?: 'right' | 'bottom' | 'full'; children: ReactNode; pied?: ReactNode; sousTitre?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ouverte || typeof document === 'undefined') return;
    const avant = document.activeElement as HTMLElement | null;
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFermer();
      if (e.key === 'Tab' && ref.current) piegeDeFocus(ref.current, e);
    };
    document.addEventListener('keydown', surTouche);
    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => {
      ref.current?.querySelector<HTMLElement>('button, a[href], input, select, textarea')?.focus();
    }, 40);
    return () => {
      document.removeEventListener('keydown', surTouche);
      document.body.style.overflow = debordement;
      avant?.focus?.();
    };
  }, [ouverte, onFermer]);

  if (!ouverte || typeof document === 'undefined') return null;

  const positions = {
    right: 'inset-y-0 right-0 h-full w-[min(420px,92vw)] rounded-l-[24px]',
    bottom: 'inset-x-0 bottom-0 max-h-[88vh] w-full rounded-t-[24px]',
    full: 'inset-0 h-full w-full',
  }[cote];

  return createPortal(
    <div className="fixed inset-0 z-[80]" role="presentation">
      <div className="sheet-scrim" onClick={onFermer} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={titre}
        className={cn('sheet', positions)}>
        <header className="flex items-start justify-between gap-4 border-b border-lin-200 px-5 py-4">
          <div>
            <h2 className="font-display text-xl leading-tight text-encre-900">{titre}</h2>
            {sousTitre && <p className="mt-0.5 text-[0.8125rem] text-encre-500">{sousTitre}</p>}
          </div>
          <IconeBouton nom="close" libelle="Fermer" onClick={onFermer} className="-mr-2 -mt-1 shrink-0" />
        </header>
        <div className="sheet__body">{children}</div>
        {pied && <footer className="sheet__foot">{pied}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/* ================================================================= ACCORDÉON */

export function Accordéon({ titre, children, ouvertParDefaut }: { titre: string; children: ReactNode; ouvertParDefaut?: boolean }) {
  return (
    <details className="group border-b border-lin-200 py-1" open={ouvertParDefaut}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-[0.9375rem] font-medium text-encre-900 [&::-webkit-details-marker]:hidden">
        {titre}
        <Icon name="chevronDown" size={18} className="shrink-0 text-encre-500 transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <div className="-mt-1 pb-5 text-[0.9375rem] leading-relaxed text-encre-600">{children}</div>
    </details>
  );
}

/* ==================================================================== VIDES */

export function Vide({ icone = 'search', titre, texte, action }: {
  icone?: IconName; titre: string; texte?: string; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-full bg-lin-100 text-encre-500">
        <Icon name={icone} size={26} />
      </span>
      <h2 className="mt-4 font-display text-xl text-encre-900">{titre}</h2>
      {texte && <p className="mt-2 max-w-sm text-[0.9375rem] leading-relaxed text-encre-500">{texte}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ====================================================================== TOASTS */

interface Message { id: number; texte: string; action?: { libelle: string; faire: () => void } }
const ToastCtx = createContext<{ annoncer: (m: Omit<Message, 'id'>) => void }>({ annoncer: () => {} });

export function FournisseurToasts({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const annoncer = useCallback((m: Omit<Message, 'id'>) => {
    const id = Date.now();
    setMessages(prev => [...prev.slice(-2), { ...m, id }]);
    window.setTimeout(() => setMessages(prev => prev.filter(x => x.id !== id)), 5600);
  }, []);

  return (
    <ToastCtx.Provider value={{ annoncer }}>
      {children}
      <div aria-live="polite" aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-24 z-[90] flex flex-col items-center gap-2 px-4 lg:bottom-8">
        {messages.map(m => (
          <div key={m.id}
            className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-encre-900 px-4 py-3 text-lin-50 shadow-float">
            <Icon name="check" size={18} className="shrink-0 text-success-light" />
            <p className="flex-1 text-[0.875rem] leading-snug">{m.texte}</p>
            {m.action && (
              <button type="button" onClick={() => { m.action!.faire(); setMessages([]); }}
                className="shrink-0 rounded-full bg-lin-50/12 px-3 py-1.5 text-[0.8125rem] font-semibold text-lin-50 transition hover:bg-lin-50/20">
                {m.action.libelle}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToasts = () => useContext(ToastCtx);
